/**
 * The per-video design system, as the tokens a composition is judged against. One video authors one
 * of these before its first graphic; every composition then inherits it (the CSS is injected into
 * each document) and the design-system lint rules read it back from here.
 */

/** The type scale: three sizes, largest first, and every piece of copy uses one of them. */
export const TYPE_TOKENS = ['--ds-type-hero', '--ds-type-headline', '--ds-type-label'] as const;

/** The one chart box every data graphic is drawn in, with the edge its series sit on. */
export const PLOT_TOKENS = [
    '--ds-plot-x',
    '--ds-plot-y',
    '--ds-plot-w',
    '--ds-plot-h',
    '--ds-baseline-y',
] as const;

export const REQUIRED_DESIGN_TOKENS = [
    ...TYPE_TOKENS,
    '--ds-margin',
    '--ds-gutter',
    '--ds-stroke',
    '--ds-radius',
    ...PLOT_TOKENS,
    '--ds-accent',
    '--ds-ink',
    '--ds-bg',
] as const;

export type DesignTokens = Map<string, string>;

/**
 * The `--ds-*` declarations in a stylesheet, as name → raw value. A flat scan rather than a parse:
 * the system is a single `:root` block, and a declaration that lands in some other rule still tells
 * the rules what value the author chose.
 */
export function parseDesignTokens(css: string): DesignTokens {
    const tokens: DesignTokens = new Map();
    const declaration = /(--ds-[\w-]+)\s*:\s*([^;}]+)/g;
    let match: RegExpExecArray | null;
    while ((match = declaration.exec(css)) !== null) {
        tokens.set(match[1], match[2].trim());
    }
    return tokens;
}

/** A token's value in px, or null when it isn't a length (a color, a `var()` reference). */
export function tokenPx(tokens: DesignTokens, name: string): number | null {
    const raw = tokens.get(name);
    if (raw === undefined) return null;
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : null;
}

export interface PlotBox {
    x: number;
    y: number;
    width: number;
    height: number;
    baselineY: number;
}

/** The declared plot box, or null when the system doesn't give every side of it as a length. */
export function plotBoxOf(tokens: DesignTokens): PlotBox | null {
    const x = tokenPx(tokens, '--ds-plot-x');
    const y = tokenPx(tokens, '--ds-plot-y');
    const width = tokenPx(tokens, '--ds-plot-w');
    const height = tokenPx(tokens, '--ds-plot-h');
    const baselineY = tokenPx(tokens, '--ds-baseline-y');
    if (x === null || y === null || width === null || height === null || baselineY === null) {
        return null;
    }
    return { x, y, width, height, baselineY };
}

/** The type scale in px, largest first, skipping any size that isn't a length. */
export function typeScalePx(tokens: DesignTokens): number[] {
    return TYPE_TOKENS.map((name) => tokenPx(tokens, name)).filter(
        (size): size is number => size !== null,
    );
}
